// Phase 4 — Top-left bell icon + popover.
// Replaces the standalone Notification Center page so notifications
// are always one click away from anywhere in the app.
//
// Click the bell → popover shows the user's recent notifications.
// Opening the popover stamps lastSeenId via the same helper used by
// the unread-badge hook, so the badge clears instantly.

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Bell, Trophy, ClipboardList, Wine, Cookie, GlassWater,
  MessageCircle, Megaphone, CheckCircle2
} from "lucide-react";
import { useUnreadNotifications } from "./useUnreadNotifications";
import { setLastSeenNotifId, getLastSeenNotifId } from "../views/Notifications";

const ICON_MAP = [
  { match: /SQUAD|SELECTED|XI|ROSTER/i,                Icon: ClipboardList, color: "var(--gold)" },
  { match: /MATCH RESULT|TOP PERFORMER|WON|LOST/i,     Icon: Trophy,        color: "var(--gold)" },
  { match: /DRINKS|VOLUNTEER FOR DRINKS|VOLUNTEERED/i, Icon: Wine,          color: "var(--blue)" },
  { match: /SNACK/i,                                   Icon: Cookie,        color: "var(--orange)" },
  { match: /ALCOHOL|BEER/i,                            Icon: GlassWater,    color: "var(--orange)" },
  { match: /MESSAGE|CHAT|MENTION/i,                    Icon: MessageCircle, color: "var(--blue)" },
  { match: /ANNOUNCEMENT|UPDATE/i,                     Icon: Megaphone,     color: "var(--gold)" }
];
function iconFor(message) {
  for (const e of ICON_MAP) if (e.match.test(message || "")) return e;
  return { Icon: Bell, color: "var(--muted)" };
}

// Map a notification type → in-app nav key (see Module.jsx). Drives
// the "click a row to jump there" UX. Falls back to type-agnostic
// keyword sniffing against the message body so legacy rows
// (Channel = "EMAIL" / "WHATSAPP") still route somewhere sensible.
const TYPE_TO_VIEW = {
  SQUAD_PICK:        "squad",
  SQUAD_ADDED:       "squad",
  VOLUNTEER_DRINKS:  "availability",
  VOLUNTEER_SNACKS:  "squad",
  VOLUNTEER_ALCOHOL: "squad",
  MATCH_RESULT:      "schedule",
  MESSAGE:           "messages",
  MENTION:           "messages",
  ANNOUNCEMENT:      "announcements"
};
function targetFor({ type, message }) {
  if (type && TYPE_TO_VIEW[type]) return TYPE_TO_VIEW[type];
  const m = message || "";
  if (/squad|playing xi|roster/i.test(m))      return "squad";
  if (/availab|drinks|volunteer/i.test(m))     return "availability";
  if (/match result|score|won|lost/i.test(m))  return "schedule";
  if (/chat|message|mention/i.test(m))         return "messages";
  if (/announcement/i.test(m))                 return "announcements";
  return null;   // unknown → don't navigate, just close popover
}

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diffMin = Math.round((Date.now() - d) / 60000);
  if (diffMin < 1)  return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24)  return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7)  return `${diffDay}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function NotificationBell({ notifications, onNavigate, compact = false }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const unread = useUnreadNotifications(notifications);

  const items = useMemo(() => {
    return [...(notifications || [])]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [notifications]);

  // Click outside / Esc to dismiss.
  useEffect(() => {
    if (!open) return;
    const onDown = e => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    const onKey  = e => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown",   onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown",   onKey);
    };
  }, [open]);

  // Mark everything currently visible as seen the moment the popover opens.
  useEffect(() => {
    if (!open || items.length === 0) return;
    const maxId = items.reduce((m, n) => Math.max(m, Number(n.id) || 0), 0);
    if (maxId > getLastSeenNotifId()) setLastSeenNotifId(maxId);
  }, [open, items]);

  const lastSeen = getLastSeenNotifId();

  return (
    <div className={`notif-bell-wrap${compact ? " notif-bell-wrap--compact" : ""}`} ref={wrapRef}>
      <button
        type="button"
        className={`notif-bell${open ? " notif-bell--open" : ""}`}
        onClick={() => setOpen(o => !o)}
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : "Notifications"}
        aria-expanded={open}
        title="Notifications"
      >
        <Bell size={compact ? 18 : 18}/>
        {unread > 0 && (
          <span className="notif-bell-badge">{unread > 99 ? "99+" : unread}</span>
        )}
      </button>

      {open && (
        <div className="notif-pop" role="dialog" aria-label="Notifications">
          <header className="notif-pop-head">
            <span className="notif-pop-title"><Bell size={14}/> Notifications</span>
            {items.length > 0 && (
              <span className="notif-pop-count">{items.length}</span>
            )}
          </header>
          {items.length === 0 ? (
            <div className="notif-pop-empty">
              <Bell size={28}/>
              <p>No notifications yet.</p>
              <small>You'll see updates here when you're picked in a playing XI, when you volunteer for a game, or when match results are recorded.</small>
            </div>
          ) : (
            <ul className="notif-pop-list">
              {items.slice(0, 30).map(n => {
                const { Icon, color } = iconFor(n.message);
                const wasUnread = Number(n.id) > lastSeen;
                const target = targetFor(n);
                const clickable = !!target && !!onNavigate;
                return (
                  <li
                    key={n.id}
                    className={
                      "notif-pop-row"
                      + (wasUnread ? " notif-pop-row--unread" : "")
                      + (clickable ? " notif-pop-row--clickable" : "")
                    }
                    onClick={() => {
                      if (!clickable) return;
                      setOpen(false);
                      onNavigate(target);
                    }}
                    onKeyDown={(e) => {
                      if (!clickable) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setOpen(false);
                        onNavigate(target);
                      }
                    }}
                    role={clickable ? "button" : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    aria-label={clickable ? `${n.message} — opens ${target}` : undefined}
                  >
                    <span className="notif-pop-icon" style={{ color }}>
                      <Icon size={14}/>
                    </span>
                    <div className="notif-pop-body">
                      <div className="notif-pop-msg">{n.message}</div>
                      <div className="notif-pop-meta">
                        <small>{fmtWhen(n.createdAt)}</small>
                        {!wasUnread && (
                          <small className="notif-pop-seen">
                            <CheckCircle2 size={9}/> Seen
                          </small>
                        )}
                      </div>
                    </div>
                    {clickable && (
                      <span className="notif-pop-chev" aria-hidden="true">›</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {items.length > 30 && (
            <footer className="notif-pop-foot">
              Showing the latest 30 of {items.length}.
            </footer>
          )}
        </div>
      )}
    </div>
  );
}
